import { History, Receipt } from "@mui/icons-material";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";

interface EnrollmentTypeChangeDialogProps {
    enrollmentChangeDialog: number;
    setEnrollmentChangeDialog: React.Dispatch<React.SetStateAction<number>>;
    onUpdate: () => void;
}

export default function EnrollmentTypeChangeDialog({ enrollmentChangeDialog, setEnrollmentChangeDialog, onUpdate }: EnrollmentTypeChangeDialogProps) {
    return (
        <Dialog
            open={enrollmentChangeDialog !== -1}
            onClose={() => setEnrollmentChangeDialog(-1)}
            aria-labelledby="enrollment-change-dialog"
            aria-describedby="enrollment-change-dialog-description"
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    overflow: 'visible'
                }
            }}
        >

            <Box sx={{ position: 'relative', textAlign: 'center', pt: 3, pb: 1 }}>
                <DialogTitle 
                    id="enrollment-change-dialog-title"
                    sx={{ 
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: '#1e293b',
                        textAlign: 'center',
                        pb: 1,
                        pt: 0
                    }}
                >
                    {enrollmentChangeDialog === 1
                    ? "Switch to Recurring Enrollment?"
                    : "Switch to One-Time Enrollment?"}
                </DialogTitle>
            </Box>
            
             <DialogContent sx={{ pt: 1, pb: 2 }}>
                <DialogContentText 
                    id="enrollment-change-dialog-description"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        textAlign: 'center',
                        mb: 2
                    }}
                >
                    {enrollmentChangeDialog === 1 ? (
                        <>
                            You're about to enable recurring payments for this classroom.
                            </>
                        ) : (
                            <>
                            You're about to switch this classroom to one-time payments.
                        </>
                    )}
                </DialogContentText>

                <Stack spacing={2}>
                    <Box sx={{ 
                        backgroundColor: '#f0f9ff', 
                        border: '1px solid #e0f2fe',
                        borderRadius: 2, 
                        p: 1 
                    }}>
                        
                        <Stack spacing={1.5}>
                            <List dense disablePadding>
                                <ListItem disableGutters>
                                <ListItemText
                                    primary={
                                    <Typography variant="body2">
                                        {enrollmentChangeDialog === 1
                                        ? '• Existing Students will be charged automatically every 30 days'
                                        : '• New Students will pay once for lifetime access'}
                                    </Typography>
                                    }
                                />
                                </ListItem>

                                <ListItem disableGutters>
                                <ListItemText
                                    primary={
                                    <Typography variant="body2">
                                        {enrollmentChangeDialog === 1
                                        ? '• New Students will start recurring payments from today'
                                        : '• Existing Students will stop recurring payments immediately'}
                                    </Typography>
                                    }
                                />
                                </ListItem>
                            </List>
                            </Stack>
                    </Box>
                </Stack>
            </DialogContent>
    
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2, justifyContent: 'center' }}>
                <Button
                    onClick={() => setEnrollmentChangeDialog(-1)}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        py: 1,
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb'
                        }
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={() => {
                        onUpdate();
                        setEnrollmentChangeDialog(-1);
                    }}
                    variant="contained"
                    sx={{ 
                        backgroundColor: "#10b981",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1,
                        boxShadow: '0 4px 12px -2px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                            backgroundColor: "#059669",
                            boxShadow: '0 8px 20px -4px rgba(16, 185, 129, 0.4)',
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Continue
                </Button>
            </DialogActions>

        </Dialog>
    );
}